import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import { CaretUp, CaretDown, CaretUpDown } from "@phosphor-icons/react";
import { formatDate as usDate } from "@/lib/dates";

// Session-persisted list state: filters, search and sorting survive opening a
// record and returning to the list.
export function useListState(pageKey, defaults) {
  const [state, setState] = useState(() => {
    try {
      return { ...defaults, ...JSON.parse(sessionStorage.getItem(`list-state:${pageKey}`) || "{}") };
    } catch (e) {
      return defaults;
    }
  });
  useEffect(() => {
    sessionStorage.setItem(`list-state:${pageKey}`, JSON.stringify(state));
  }, [pageKey, state]);
  return [state, setState];
}

const TEMP_ORDER = { cold: 0, warm: 1, hot: 2 };
const STAGE_ORDER = { cold: 1, warm: 2, hot: 3 };

// Case-insensitive, whitespace-tolerant string key for sorting.
const norm = (s) => (s || "").trim().toLowerCase();

// Sort across the FULL matching dataset (the API returns all matches, unpaged).
// Strings compare via localeCompare on trimmed, lowercased values so the full
// organization name drives the order ("ABC Company, Inc." < "First Citizens
// Bank & Trust Co.") regardless of casing or stray spaces. Ties always break
// on organization name for a stable, deterministic order across pages.
export function sortRows(rows, sortKey, sortDir) {
  const get = (r) => {
    switch (sortKey) {
      case "organization": return norm(r.organization_name);
      case "contact": return r.primary_contact ? norm(`${r.primary_contact.first_name} ${r.primary_contact.last_name}`) : "";
      case "temperature": return TEMP_ORDER[r.effective_temperature || r.lead_temperature] ?? -1;
      case "stage": return r.lifecycle_status === "client" ? 4 : (STAGE_ORDER[r.relationship_stage] || 0);
      case "source": return norm(r.lead_source_name);
      case "salesperson": return norm(r.assigned_salesperson_name);
      case "properties": return r.property_count || 0;
      case "added": return r.date_entered || "";
      default: return "";
    }
  };
  const dir = sortDir === "desc" ? -1 : 1;
  const cmp = (va, vb) => {
    if (typeof va === "string" || typeof vb === "string") {
      return String(va).localeCompare(String(vb), "en", { numeric: true });
    }
    return va < vb ? -1 : va > vb ? 1 : 0;
  };
  return [...rows].sort((a, b) => {
    const primary = cmp(get(a), get(b));
    if (primary !== 0) return primary * dir;
    // Stable tie-break: organization A–Z, independent of sort direction.
    return cmp(norm(a.organization_name), norm(b.organization_name));
  });
}

export function SortHeader({ label, sortKey, sort, onSort, className = "", testId }) {
  const active = sort.sortKey === sortKey;
  const Icon = !active ? CaretUpDown : sort.sortDir === "asc" ? CaretUp : CaretDown;
  return (
    <button type="button" onClick={() => onSort(sortKey)}
      aria-sort={active ? (sort.sortDir === "asc" ? "ascending" : "descending") : "none"}
      className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${active ? "text-[#032852]" : "text-[#6D6D70]"} hover:text-[#032852] ${className}`}
      data-testid={testId}>
      {label} <Icon size={12} weight="bold" className={active ? "text-[#569A1F]" : "opacity-50"} />
    </button>
  );
}

const TEMP_STYLES = {
  cold: "bg-[#3B82F6]/10 text-[#1D4ED8]",
  warm: "bg-[#F59E0B]/10 text-[#B45309]",
  hot: "bg-[#EF4444]/10 text-[#B91C1C]",
};
const TEMP_LABELS = { cold: "Cold", warm: "Warm", hot: "Hot" };

export function TemperatureBadge({ code, testId }) {
  if (!code) return null;
  return (
    <Badge
      data-testid={testId}
      className={`rounded-sm border-0 text-xs font-semibold hover:bg-transparent ${TEMP_STYLES[code] || ""}`}
    >
      {TEMP_LABELS[code] || code}
    </Badge>
  );
}

const STATUS_STYLES = {
  active: "bg-[#569A1F]/10 text-[#457d18]",
  inactive: "bg-[#6D6D70]/10 text-[#6D6D70]",
  archived: "bg-[#6D6D70]/10 text-[#6D6D70]",
};

export function StatusBadge({ status, testId }) {
  return (
    <Badge
      data-testid={testId}
      className={`rounded-sm border-0 text-xs font-semibold capitalize hover:bg-transparent ${STATUS_STYLES[status] || STATUS_STYLES.active}`}
    >
      {status || "active"}
    </Badge>
  );
}

export function LifecycleBadge({ status }) {
  return status === "client" ? (
    <Badge className="rounded-sm border-0 bg-[#569A1F] text-xs font-bold text-white hover:bg-[#569A1F]" data-testid="lifecycle-badge">
      Client
    </Badge>
  ) : (
    <Badge className="rounded-sm border-0 bg-[#032852]/10 text-xs font-semibold text-[#032852] hover:bg-[#032852]/10" data-testid="lifecycle-badge">
      Prospect
    </Badge>
  );
}

// Single lifecycle stage cue (stage name + color, never color alone).
// Clients derive TFS green from lifecycle_status. Blank or legacy stored values
// (old target/engaged/qualified) show neutral "Needs review" until a user
// explicitly saves a current stage — never auto-converted.
const STAGE_STYLES = {
  cold: "bg-[#3B82F6]/10 text-[#1D4ED8]",     // blue
  warm: "bg-[#F59E0B]/15 text-[#B45309]",     // amber/orange
  hot: "bg-[#EF4444]/10 text-[#B91C1C]",      // red
};
const STAGE_LABELS = { cold: "Cold", warm: "Warm", hot: "Hot" };

// Solid accent colors for the thin stage bars (detail header / list rows).
// Same palette as the badges; "Needs review" stays neutral gray.
const STAGE_ACCENTS = { cold: "#3B82F6", warm: "#F59E0B", hot: "#EF4444" };
export function stageAccent(stage, lifecycle) {
  if (lifecycle === "client") return "#569A1F";
  return STAGE_ACCENTS[stage] || "#A1A1AA";
}

export function RelationshipStageBadge({ stage, lifecycle, reviewPending, onReviewClick, testId }) {
  let badge;
  if (lifecycle === "client") {
    badge = (
      <Badge data-testid={testId} className="rounded-sm border-0 bg-[#569A1F] text-xs font-bold text-white hover:bg-[#569A1F]">
        Client
      </Badge>
    );
  } else if (!stage || !STAGE_LABELS[stage]) {
    badge = (
      <Badge
        data-testid={testId}
        title="Needs review: Lifecycle Stage is not set. Select Cold, Warm, or Hot based on documented relationship evidence."
        onClick={onReviewClick}
        className={`rounded-sm border border-dashed border-[#A1A1AA] bg-transparent text-xs font-semibold text-[#6D6D70] hover:bg-transparent ${onReviewClick ? "cursor-pointer underline-offset-2 hover:underline" : ""}`}
      >
        Needs review
      </Badge>
    );
  } else {
    badge = (
      <Badge data-testid={testId} className={`rounded-sm border-0 text-xs font-semibold hover:bg-transparent ${STAGE_STYLES[stage]}`}>
        {STAGE_LABELS[stage]}
      </Badge>
    );
  }
  // An opportunity closed with no clear active pursuit — stage retained, user review requested.
  if (!reviewPending || lifecycle === "client") return badge;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {badge}
      <Badge
        title="An opportunity closed — confirm this stage is still accurate"
        onClick={onReviewClick}
        className={`rounded-sm border border-dashed border-[#B45309] bg-transparent text-[10px] font-semibold text-[#B45309] hover:bg-transparent ${onReviewClick ? "cursor-pointer underline-offset-2 hover:underline" : ""}`}
        data-testid={testId ? `${testId}-review` : undefined}
      >
        Review
      </Badge>
    </span>
  );
}

export function FieldLabel({ children }) {
  return (
    <span className="text-xs font-bold uppercase tracking-wide text-[#52525B]">{children}</span>
  );
}

// Delegates to the shared US-format utility (MM/DD/YYYY); keeps the "—"
// placeholder existing list/detail screens rely on for blank dates.
export function formatDate(iso) {
  return iso ? usDate(iso) : "—";
}
